import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

/**
 * Client-side structured logger for production debugging.
 *
 * The browser can't emit to Cloud Logging directly, so severe events are
 * mirrored into a Firestore `clientErrors/` collection. Admins read; no
 * one else. Each entry carries a session-scoped `traceId` that the
 * server includes on every matching request so a single user action can
 * be followed across the wire.
 *
 * Routine logs still print to the browser console — we're not trying to
 * replace devtools, just capture the failures a prod support engineer
 * needs.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const SESSION_TRACE_ID =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/** Header name clients send + servers receive. Mirrors GCP conventions. */
export const TRACE_HEADER = 'X-Request-Id';

/** Stable per-tab trace id. Exposed on window for copy-paste into bug reports. */
export function getTraceId(): string {
  return SESSION_TRACE_ID;
}

interface LogPayload {
  level: LogLevel;
  message: string;
  context?: Record<string, unknown>;
  error?: { name: string; message: string; stack?: string };
  requestId?: string;
}

const ERRORS = collection(db, 'clientErrors');

function serializeError(err: unknown): LogPayload['error'] {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }
  if (typeof err === 'string') return { name: 'Error', message: err };
  return { name: 'Error', message: JSON.stringify(err).slice(0, 500) };
}

async function sink(payload: LogPayload): Promise<void> {
  if (payload.level !== 'warn' && payload.level !== 'error') return;
  try {
    await addDoc(ERRORS, {
      level: payload.level,
      message: payload.message,
      context: payload.context ?? null,
      error: payload.error ?? null,
      traceId: SESSION_TRACE_ID,
      requestId: payload.requestId ?? null,
      actorUid: auth.currentUser?.uid ?? null,
      actorEmail: auth.currentUser?.email ?? null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      url: typeof location !== 'undefined' ? location.href : null,
      at: serverTimestamp(),
    });
  } catch {
    // If Firestore is unreachable, still fall through to console.
  }
}

function consoleOut(level: LogLevel, message: string, context?: unknown) {
  const fn =
    level === 'error' ? console.error
    : level === 'warn' ? console.warn
    : level === 'info' ? console.info
    : console.debug;
  fn(`[jam:${level}] ${message}`, context ?? '');
}

export const logger = {
  debug(message: string, context?: Record<string, unknown>) {
    consoleOut('debug', message, context);
  },
  info(message: string, context?: Record<string, unknown>) {
    consoleOut('info', message, context);
  },
  warn(message: string, context?: Record<string, unknown>) {
    consoleOut('warn', message, context);
    void sink({ level: 'warn', message, context });
  },
  error(message: string, err?: unknown, context?: Record<string, unknown>) {
    consoleOut('error', message, { ...context, err });
    void sink({
      level: 'error',
      message,
      context,
      error: err != null ? serializeError(err) : undefined,
    });
  },
};

/**
 * Wire browser-level error handlers once at app boot.
 * Captures unhandled errors + rejection + fetch failures so support can
 * see what the user saw without asking them to open devtools.
 */
export function installGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    logger.error('window.onerror', event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('unhandledrejection', event.reason);
  });
  // Expose trace id on window for easy copy from devtools.
  (window as unknown as { __jamTraceId: string }).__jamTraceId = SESSION_TRACE_ID;
}
